import * as React from "react";
import { styled, IconButton, Link } from "@mui/material";
import { ReactComponent as Send } from "../../assets/sendIcon.svg";
import { useCallback } from "react";
import useSessionStore from "../../context/useSessionStore";
import { useTranslateByGender } from "fe-common/locale/useTranslateByGender";

//region [[ Styles ]]

const CustomMessageInputView = styled("div")({
  width: "100%",
  maxWidth: 1100,
  margin: "auto",
});
const InputContainer = styled("div")(({ theme }) => ({
  display: "flex",
  alignItems: "center",
  margin: "auto",
  border: "1px solid " + theme.palette.divider,
  borderRadius: 30,
  padding: "10px 10px 10px 30px",
  background: "#FFFFFF",
  gap: 20,
  "& ::placeholder": {
    color: "darkGray",
  },
  "&:dir(rtl)": {
    padding: "10px 30px 10px 10px",
  },
  [theme.breakpoints.down("sm")]: {
    padding: "8px 6px 8px 20px",
    "&:dir(rtl)": {
      padding: "8px 20px 8px 6px",
    },
  },
}));
const InputTextField = styled("input")(({ theme }) => ({
  width: "100%",
  background: "transparent",
  border: "none",
  fontFamily: theme.typography.fontFamily,
  fontSize: 14,
  lineHeight: 1.2,
  padding: 0,
  outline: "none",
  ".embedded-view &": {
    fontSize: 13,
  },
}));
const StyledIconButton = styled(IconButton)(({ theme }) => ({
  color: theme.palette.primary.main,
  "& svg": { fill: "currentColor", maxHeight: "100%" },
  ".embedded-view &": {
    height: 12,
    padding: 0,
  },
  "&:dir(rtl)": {
    transform: "rotate(180deg)",
  },
}));
const Disclaimer = styled("div")(({ theme }) => ({
  fontSize: 11,
  color: theme.palette.text.secondary,
  padding: "8px 34px 0 34px",
  [theme.breakpoints.down("sm")]: {
    padding: "8px 20px 0 24px",
    "&:dir(rtl)": {
      padding: "8px 24px 0 20px",
    },
  },
}));

//endregion [[ Styles ]]

//region [[ Props ]]

export interface CustomMessageInputProps {
  onSend: (text: string) => void;
  disabled?: boolean;
}

//endregion [[ Props ]]

//region [[ Functions ]]

//endregion [[ Functions ]]

export const CustomMessageInput = ({ ...props }: CustomMessageInputProps) => {
  const [text, setText] = React.useState("");
  const embedded = useSessionStore().ragSettings?.embeddedView;
  const { translate } = useTranslateByGender();
  const handleSend = useCallback(() => {
    props.onSend(text);
    setText("");
  }, [props, text]);

  return (
    <CustomMessageInputView>
      <InputContainer className={embedded ? "embedded-view" : ""}>
        <InputTextField
          type="text"
          value={text}
          placeholder={translate("DIALOGUE_INPUT_PLACEHOLDER")}
          onKeyDown={(e) => {
            if (e.key === "Enter") handleSend();
          }}
          onChange={(e) => setText(e.target.value)}
          disabled={props.disabled}
        />
        <StyledIconButton onClick={handleSend} disabled={props.disabled}>
          <Send />
        </StyledIconButton>
      </InputContainer>
      {!embedded && (
        <Disclaimer>
          Answers are supported by OpenAI LLM tools.{" "}
          <Link
            href="https://www.kahun.com/terms-of-service#third-party"
            target="_blank"
          >
            Read more
          </Link>
        </Disclaimer>
      )}
    </CustomMessageInputView>
  );
};
